import React from "react";
import {
  Box,
  Button,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StatusChip from "../../../../components/common/StatusChip";
import ChipList from "../../../../components/common/ChipList";
import { formatEpochDate } from "../../utils/normalizeUser";

const DetailItem = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, mt: 0.25, wordBreak: "break-word" }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const UserDetailsDrawer = ({
  open,
  user,
  onClose,
  onChangePassword,
  onUpdateRole,
  onEdit,
  onDelete,
  onUnlock,
  onToggleMfa,
  mfaSaving = false,
}) => {
  if (!user) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 3,
      }}
      PaperProps={{
        sx: (theme) => {
          const topOffset = theme.spacing(8);
          return {
            width: { xs: "100%", sm: 440 },
            top: topOffset,
            height: `calc(100% - ${topOffset})`,
            maxHeight: `calc(100vh - ${topOffset})`,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxSizing: "border-box",
          };
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
          flexShrink: 0,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0, pr: 1 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
              User Details
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ wordBreak: "break-word", mt: 0.25 }}
            >
              {user.username}
            </Typography>
          </Box>
          <IconButton aria-label="Close details" onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          px: 3,
          pt: 2,
          pb: 2.5,
        }}
      >
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <DetailItem label="User ID" value={user.userId} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Username" value={user.username} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="First Name" value={user.firstname} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Last Name" value={user.lastname} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Email" value={user.email} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Mobile" value={user.mobile} />
              </Grid>
              <Grid item xs={12} sm={6}>
                {onToggleMfa ? (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      MFA
                    </Typography>
                    <Box sx={{ mt: 0.25 }}>
                      <FormControlLabel
                        sx={{ ml: 0, minHeight: 32 }}
                        control={
                          <Switch
                            size="small"
                            checked={Boolean(user.mfaEnabled)}
                            onChange={(event) =>
                              onToggleMfa(event.target.checked)
                            }
                            disabled={mfaSaving}
                          />
                        }
                        label={
                          user.mfaEnabled ? "MFA enabled" : "MFA disabled"
                        }
                      />
                    </Box>
                  </Box>
                ) : (
                  <DetailItem
                    label="MFA"
                    value={user.mfaEnabled ? "Enabled" : "Disabled"}
                  />
                )}
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Account
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <StatusChip enabled={user.enabled} locked={user.locked} />
                </Box>
              </Grid>
              {user.type ? (
                <Grid item xs={12} sm={6}>
                  <DetailItem label="Type" value={user.type} />
                </Grid>
              ) : null}
              <Grid item xs={12} sm={6}>
                <DetailItem
                  label="Created Date"
                  value={formatEpochDate(user.creationDate)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem
                  label="Updated Date"
                  value={formatEpochDate(user.lastModifiedDate)}
                />
              </Grid>
              {user.lastLoggedInTs ? (
                <Grid item xs={12}>
                  <DetailItem
                    label="Last Logged In"
                    value={formatEpochDate(user.lastLoggedInTs)}
                  />
                </Grid>
              ) : null}
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Roles
            </Typography>
            <ChipList
              items={user.roles}
              maxVisible={null}
              emptyLabel="No roles assigned"
              sx={{ flexWrap: "wrap", rowGap: 0.75, overflow: "visible" }}
            />
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          px: 3,
          py: 2,
          flexShrink: 0,
          borderTop: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        <Stack spacing={1.25}>
          {onDelete ? (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="outlined"
                color="error"
                onClick={onDelete}
                sx={{ whiteSpace: "nowrap", minWidth: 180 }}
              >
                Delete User
              </Button>
            </Box>
          ) : null}

          <Grid container spacing={1.25}>
            {onEdit ? (
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={onEdit}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Edit User
                </Button>
              </Grid>
            ) : null}
            {onUpdateRole ? (
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={onUpdateRole}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Update Role
                </Button>
              </Grid>
            ) : null}
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                onClick={onChangePassword}
                sx={{ whiteSpace: "nowrap" }}
              >
                Change Password
              </Button>
            </Grid>
            {onUnlock && user.locked ? (
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={onUnlock}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Unlock User
                </Button>
              </Grid>
            ) : null}
          </Grid>

          <Button fullWidth variant="text" onClick={onClose}>
            Close
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default UserDetailsDrawer;
